/**
 * Voice node ↔ session actor protocol (architecture §7.2).
 *
 * Localhost JSON over a persistent WebSocket, one connection per pod, multiplexed by
 * `session_id`. **Twelve message kinds, thirteen `type` literals** — `cancel_ack` is modelled
 * as its own discriminated message rather than as a field on `cancel`
 * (`docs/specs/voice-pipeline.md` §2). Validated by zod here and by pydantic in
 * `services/voice-node`; contract tests replay recorded traffic in CI, so the field
 * names below are snake_case on both sides and must stay identical.
 *
 * Ordering guarantees the actor relies on: `say` messages of one turn play in
 * `say_seq` order; an `engine` line never splits a narration sentence; a `cancel` is
 * acknowledged before the next `say` of the same session is accepted; a
 * `user_turn{final}` always follows its `start` with the same `turn_id`.
 */
import { z } from 'zod';
import { GameMode, Locale, ToneTag, VoiceMood } from './primitives.js';
/* ---------------------------------------------------------------- shared */
export const SessionId = z.string().min(1);
const sessionScoped = z.object({ session_id: SessionId });
export const VoiceLang = z.enum(['pt-BR', 'en-US']);
/**
 * The four values architecture §7.2's `voice_state` row carries. v1 always sends
 * `english_2026-04`, or `kokoro_en` on the Spike A fallback (§7.4, ADR-005's ADR-057
 * amendment); `portuguese_24l` and `portuguese` are the M2 siblings.
 */
export const TtsModelId = z.enum([
    'english_2026-04',
    'portuguese_24l',
    'portuguese',
    /** ADR-079: xAI text-to-speech, the v1 narrator. */
    'xai_tts',
]);
/**
 * Which `SttAdapter` implementation the voice node loads (§7.1).
 *
 * `nemotron_stream` is the v1 default under ADR-060 (speech is self-hosted end to
 * end): `nvidia/nemotron-speech-streaming-en-0.6b`, a cache-aware FastConformer
 * transducer running in the voice node next to pocket-tts. `moonshine_stream` is its
 * licence-clean fallback. `flux` (Deepgram) drops to the hosted fallback and the
 * outage path, and `groq_whisper` stays the no-partials cost floor. Which concrete
 * checkpoint a self-hosted id loads is voice-node configuration, not a wire
 * commitment — the point of the adapter is that this enum is the only thing above it
 * that has to change. Gated by Spike A3; until that gate passes, `flux` remains the
 * configured default in deployment.
 */
export const SttAdapterId = z.enum([
    'nemotron_stream',
    'moonshine_stream',
    'groq_whisper',
    /** ADR-079: xAI speech-to-text, the v1 transcriber. */
    'xai_stt',
]);
/* --------------------------------------------------------- node → actor */
/** Deterministic tone features, in parallel with STT, ready at end of turn (§7.1). */
export const ToneScores = z.object({
    rms_rel_db: z.number(),
    voicing: z.number().min(0).max(1),
    syl_rate: z.number().min(0),
    pitch_var: z.number().min(0),
    hesitant_p: z.number().min(0).max(1),
});
/**
 * Every optional field on a node → actor message is `nullish`, not `optional`: the node
 * is a pydantic process, and a model that carries `None` is one `exclude_none` flag away
 * from putting `null` on the wire. It did, from the first commit until 2026-09-14 —
 * `"tags": null, "scores": null, "interrupted": null` on every `user_turn` — and zod's
 * `optional()` rejects `null`, so the pod dropped every spoken turn a player ever said,
 * silently. The node now omits absent fields (`dump_voice_message`), and a `null` that
 * reaches here anyway is read as "absent" instead of costing the player their words.
 */
export const UserTurn = sessionScoped.extend({
    type: z.literal('user_turn'),
    turn_id: z.string(),
    /** `start` at speech onset / PTT press; `final` carries the transcript. */
    phase: z.enum(['start', 'final']),
    text: z.string().nullish(),
    lang: VoiceLang.nullish(),
    tags: z.array(ToneTag).nullish(),
    scores: ToneScores.nullish(),
    t_start: z.number(),
    /** PTT release, the anchor for the voice-to-voice KPI. */
    t_release: z.number().nullish(),
    t_final: z.number().nullish(),
    stt_ms: z.number().nullish(),
    /** `roll` is the spoken throw while `awaiting_roll`; `ooc` goes to the Guia. */
    kind: z.enum(['speech', 'roll', 'ooc']),
    /** SHA-256 of the utterance PCM, supplied as the player's throw entropy. */
    entropy: z.string().regex(/^[0-9a-f]{64}$/).nullish(),
    /** Present when this `start` interrupted the GM mid-sentence. */
    interrupted: z
        .object({ say_seq: z.number().int(), spoken_chars: z.number().int().min(0) })
        .nullish(),
});
/** The roll prompt for `roll_id` finished playing; timers start from `t_end`. */
export const RollRequestSpoken = sessionScoped.extend({
    type: z.literal('roll_request_spoken'),
    roll_id: z.string(),
    say_seq: z.number().int().nonnegative(),
    t_end: z.number(),
});
/**
 * ADR-112: an utterance finished playing to the player — the last `say` of `turn_id`,
 * its queued audio drained. Not sent for a cancelled turn. The prologue turns its
 * pages on it; nothing else waits for it yet.
 */
export const SayPlayed = sessionScoped.extend({
    type: z.literal('say_played'),
    turn_id: z.string(),
    say_seq: z.number().int().nonnegative(),
    t_end: z.number(),
});
/**
 * ADR-117: this sentence has started being heard — the moment its first sample leaves
 * the node's track, not the moment it was synthesised. The pod holds a streamed turn's
 * captions on it, so the table reads a sentence as the narrator says it instead of
 * reading the whole turn while the voice is still on its first line.
 */
export const SayStarted = sessionScoped.extend({
    type: z.literal('say_started'),
    turn_id: z.string(),
    say_seq: z.number().int().nonnegative(),
    t_start: z.number(),
});
export const VoiceMetrics = sessionScoped.extend({
    type: z.literal('metrics'),
    turn_id: z.string().nullish(),
    stt_connect_ms: z.number().nullish(),
    stt_final_ms: z.number().nullish(),
    tts_ttfb_ms: z.number().nullish(),
    tts_rtf: z.number().nullish(),
    queue_depth: z.number().int().min(0),
    playout_buffer_ms: z.number().min(0),
    spoken: z
        .array(z.object({
        say_seq: z.number().int(),
        t_start: z.number(),
        t_end: z.number(),
        chars: z.number().int().min(0),
    }))
        .nullish()
        .transform((rows) => rows ?? []),
    ice_type: z.enum(['host', 'srflx', 'prflx', 'relay']).nullish(),
    jitter_ms: z.number().min(0).nullish(),
    packet_loss: z.number().min(0).max(1).nullish(),
});
/** Every 5 s; three misses mark the node unhealthy and its sessions go to text mode. */
export const VoiceHealth = z.object({
    type: z.literal('health'),
    tts_workers_up: z.number().int().min(0),
    sessions_attached: z.number().int().min(0),
    cpu_pct: z.number().min(0).max(100),
    mem_mb: z.number().min(0),
    degraded: z.enum(['captions_only']).nullish(),
});
/** Chunk-exact ack for a `cancel`; never a proportional character estimate (§7.2). */
export const CancelAck = sessionScoped.extend({
    type: z.literal('cancel_ack'),
    say_seq: z.number().int(),
    last_chunk_id: z.string(),
    char_to: z.number().int().min(0),
    chunks_dropped: z.number().int().min(0),
});
/**
 * ADR-125: what one utterance cost at the provider. `synthesized_chars` were sent to the
 * provider to read; `cached_chars` were played from the node's utterance cache and cost
 * nothing. The pod charges the pass from this, not from what it handed the node, so a turn
 * the node never read is never charged.
 */
export const TtsUsage = sessionScoped.extend({
    type: z.literal('tts_usage'),
    turn_id: z.string(),
    synthesized_chars: z.number().int().nonnegative(),
    cached_chars: z.number().int().nonnegative(),
});
export const VoiceNodeMessage = z.discriminatedUnion('type', [
    UserTurn,
    RollRequestSpoken,
    SayPlayed,
    SayStarted,
    TtsUsage,
    VoiceMetrics,
    VoiceHealth,
    CancelAck,
]);
/* --------------------------------------------------------- actor → node */
export const Say = sessionScoped.extend({
    type: z.literal('say'),
    say_seq: z.number().int().nonnegative(),
    turn_id: z.string(),
    /** One sentence, or the first clause of a turn — the words the caption shows. */
    text: z.string().min(1),
    /**
     * ADR-089: the same sentence with the narrator's delivery marks in place
     * (`[pause]`, `<whisper>…</whisper>`), when it carries any. The node speaks
     * `spoken` when present and `text` otherwise; the caption never shows a mark.
     */
    spoken: z.string().min(1).optional(),
    lang: VoiceLang,
    mood: VoiceMood,
    /**
     * ADR-089: the turn's final sentence. Sentences of one turn with one priority are
     * spoken as one utterance, so the narrator's prosody carries across them; `last`
     * is what closes it, and a change of turn or priority closes it early.
     */
    last: z.boolean().optional(),
    /** The highest committed event `seq` at generation time (§6.5). */
    watermark: z.number().int().nonnegative(),
    /** `engine` lines jump the queue at the next boundary and are not interruptible. */
    priority: z.enum(['engine', 'narration']),
    interruptible: z.boolean(),
});
/**
 * ADR-117: the turn has no more sentences. A narration is streamed sentence by
 * sentence as it is written, so the actor does not know which sentence is the last
 * one until the narrator stops writing — `last` cannot be marked on it. This closes
 * the utterance at that moment instead, rather than leaving the node to close it on
 * its idle timer, which would both hold `say_played` back and split a turn whose
 * narrator paused mid-paragraph into two utterances.
 */
export const SayEnd = sessionScoped.extend({
    type: z.literal('say_end'),
    turn_id: z.string(),
});
export const Cancel = sessionScoped.extend({
    type: z.literal('cancel'),
    /** Absent cancels every turn of the session. */
    turn_id: z.string().optional(),
    /** ADR-117: `hushed` is the player asking for silence, not speaking over the narrator. */
    reason: z.enum(['barge_in', 'governor', 'error', 'mode_change', 'hushed']),
});
/**
 * ADR-126: the narration pauses as one. On `held: true` the node stops draining its output
 * track and plays silence, keeping every queued sample and every scheduled `say_started`;
 * on `false` it resumes from the sample it stopped at. Nothing is cancelled, nothing is
 * re-synthesised. Presentation only: the pod's clocks and the tray do not read this.
 */
export const Hold = sessionScoped.extend({
    type: z.literal('hold'),
    held: z.boolean(),
});
export const Cue = sessionScoped.extend({
    type: z.literal('cue'),
    kind: z.literal('sfx'),
    id: z.string(),
    intensity: z.number().int().min(1).max(3),
    at: z.enum(['now', 'next_boundary']),
});
/** Sent on every engine mode change; combat and awaiting_roll force PTT (§7.2). */
export const VoicePolicy = sessionScoped.extend({
    type: z.literal('policy'),
    mode: GameMode,
    mic: z.enum(['ptt', 'open']),
    allow_interruptions: z.boolean(),
    /**
     * ADR-128 decision 4 (#68): the player locked the recording — the microphone is open with no
     * hand on it, for a minute at most. While this holds the node does not close the utterance on
     * its silence gate: the pauses of someone thinking aloud are one utterance and one turn, which
     * ends when the player sends. Cleared when they send, discard, or the minute stops the recording.
     */
    mic_locked: z.boolean().default(false),
    wake_phrases: z.array(z.string()).default([]),
    lang: VoiceLang,
    stt_adapter: SttAdapterId,
});
export const VoiceState = sessionScoped.extend({
    type: z.literal('voice_state'),
    narrator_id: z.string(),
    model: TtsModelId,
    /** mood → Tigris url of the exported `.safetensors` voice state. */
    moods: z.record(VoiceMood, z.string()),
    filler_bank: z.string(),
    openers: z.string(),
    /**
     * ADR-117: the language the narrator reads in. The provider takes it when the
     * connection is opened, so it belongs to this message rather than to a sentence —
     * and until 2026-09-17 nothing carried it at all: every live session, Portuguese
     * ones included, was spoken by an English-language voice.
     */
    lang: VoiceLang.default('en-US'),
    /** ADR-117: how fast the narrator reads, in the provider's 0.7–1.5. Re-sent when the player changes it. */
    speed: z.number().min(0.5).max(2).default(1),
    /** ADR-125: the persona whose voice this is, recorded beside every utterance the node caches. */
    persona_id: z.string().nullish(),
});
export const SessionActorMessage = z.discriminatedUnion('type', [
    Say,
    SayEnd,
    Cancel,
    Hold,
    Cue,
    VoicePolicy,
    VoiceState,
]);
export const VoiceMessage = z.union([VoiceNodeMessage, SessionActorMessage]);
/**
 * The wake-phrase catalog, per locale. **v1 ships `en-US` only** (ADR-057); the
 * `pt-BR` entry is the M2 catalog against the same keys and is kept here so the
 * shape is exercised from day one.
 */
export const WAKE_PHRASES = Object.freeze({
    'en-US': Object.freeze({
        roll: Object.freeze(['roll', 'roll it', 'throw', 'go ahead']),
        skip: Object.freeze(['roll for me']),
        guia: Object.freeze(['master, pause', 'hold on, guide']),
    }),
    'pt-BR': Object.freeze({
        roll: Object.freeze(['rolar', 'roda', 'joga os dados', 'pode rolar']),
        skip: Object.freeze(['pode rolar por mim', 'rola por mim']),
        guia: Object.freeze(['mestre, pausa']),
    }),
});
/** The v1 locale's throw phrases — what `policy.wake_phrases` carries in v1. */
export const ROLL_WAKE_PHRASES = WAKE_PHRASES['en-US'].roll;
/** Phrases that hand the roll back to the engine as `skip_roll {player_asked}`. */
export const ROLL_SKIP_PHRASES = WAKE_PHRASES['en-US'].skip;
/** The v1 Guia wake phrases; the session clock pauses while it answers (§3.9). */
export const GUIA_WAKE_PHRASES = WAKE_PHRASES['en-US'].guia;
//# sourceMappingURL=voice.js.map